import { cn } from '@/lib/utils';

// Color mapping for common car colors
const COLOR_MAP = {
  'black': '#1a1a1a',
  'white': '#f5f5f5',
  'silver': '#c0c0c0',
  'grey': '#808080',
  'gray': '#808080',
  'red': '#dc2626',
  'blue': '#2563eb',
  'green': '#16a34a',
  'yellow': '#eab308',
  'orange': '#ea580c',
  'purple': '#9333ea',
  'pink': '#ec4899',
  'brown': '#78350f',
  'gold': '#ca8a04',
  'beige': '#d4c4a8',
  'maroon': '#7f1d1d',
  'navy': '#1e3a5f',
  'teal': '#0d9488',
  'burgundy': '#722f37',
  'champagne': '#f7e7ce'
};

const getColorHex = (colorName) => {
  if (!colorName) return '#ec4899';
  return COLOR_MAP[colorName.toLowerCase().trim()] || '#ec4899';
};

// CSS filter to shift the pink (#ec4899, hue ~330deg) car icons to match any car color
// Neutral colors use brightness/saturation, chromatic colors use hue-rotate
const COLOR_FILTERS = {
  'black':     'saturate(0) brightness(0.15)',
  'white':     'saturate(0.05) brightness(2.2) contrast(0.7)',
  'silver':    'saturate(0.05) brightness(1.6) contrast(0.8)',
  'grey':      'saturate(0) brightness(0.65)',
  'gray':      'saturate(0) brightness(0.65)',
  'beige':     'hue-rotate(45deg) saturate(0.3) brightness(1.5)',
  'champagne': 'hue-rotate(40deg) saturate(0.2) brightness(1.6)',
  'red':       'hue-rotate(30deg) saturate(1.5) brightness(0.85)',
  'blue':      'hue-rotate(-106deg) saturate(1.3) brightness(0.8)',
  'green':     'hue-rotate(-185deg) saturate(1.2) brightness(0.75)',
  'yellow':    'hue-rotate(78deg) saturate(2) brightness(1.2)',
  'orange':    'hue-rotate(48deg) saturate(1.8) brightness(1.0)',
  'purple':    'hue-rotate(-59deg) saturate(1.3) brightness(0.7)',
  'pink':      'none',
  'brown':     'hue-rotate(54deg) saturate(0.6) brightness(0.4)',
  'gold':      'hue-rotate(73deg) saturate(1.5) brightness(1.0)',
  'maroon':    'hue-rotate(30deg) saturate(0.8) brightness(0.35)',
  'navy':      'hue-rotate(-121deg) saturate(0.8) brightness(0.35)',
  'teal':      'hue-rotate(-156deg) saturate(1.2) brightness(0.7)',
  'burgundy':  'hue-rotate(23deg) saturate(0.7) brightness(0.4)',
};

const getColorFilter = (colorName) => {
  if (!colorName) return 'none';
  return COLOR_FILTERS[colorName.toLowerCase().trim()] || 'none';
};

// Car icon definitions
const CAR_ICONS = [
  { id: 'arrow', name: 'Arrow', isArrow: true },
  { id: 'sedan', name: 'Sedan' },
  { id: 'suv', name: 'SUV' },
  { id: 'hatchback', name: 'Hatchback' },
  { id: 'van', name: 'Van' },
  { id: 'pickup', name: 'Pickup' },
  { id: 'coupe', name: 'Coupe' },
  { id: 'mpv', name: 'MPV' },
  { id: 'estate', name: 'Estate' },
  { id: 'convertible', name: 'Drop Top' },
];

// Arrow SVG for the selector
const ArrowIcon = ({ color }) => (
  <svg viewBox="0 0 20 18" className="w-full h-full">
    <path d="M10 2 L17 16 L10 12 L3 16 Z" fill={color} />
  </svg>
);

const CarIconSelector = ({ value, onChange, carColor = 'pink', disabled = false }) => {
  const colorHex = getColorHex(carColor);
  const filter = getColorFilter(carColor);

  return (
    <div className="space-y-3">
      <label className="text-sm font-medium" data-testid="car-icon-label">Choose Your Car Icon</label>
      <div className="grid grid-cols-5 gap-2">
        {CAR_ICONS.map((icon) => (
          <button
            key={icon.id}
            type="button"
            disabled={disabled}
            onClick={() => onChange(icon.id)}
            data-testid={`car-icon-${icon.id}`}
            className={cn(
              "p-1.5 rounded-xl border-2 transition-all flex flex-col items-center gap-1",
              value === icon.id
                ? "border-primary bg-primary/10 shadow-md shadow-primary/20"
                : "border-border/30 hover:border-primary/40 bg-card/30",
              disabled && "opacity-50 cursor-not-allowed"
            )}
            title={icon.name}
          >
            <div className="w-16 h-10 flex items-center justify-center overflow-hidden rounded-lg">
              {icon.isArrow ? (
                <ArrowIcon color={colorHex} />
              ) : (
                <img
                  src={`${process.env.PUBLIC_URL}/car-icons/${icon.id}.webp`}
                  alt={icon.name}
                  className="w-full h-full object-contain"
                  style={{ filter }}
                  draggable={false}
                />
              )}
            </div>
            <span className="text-[9px] text-muted-foreground truncate w-full text-center">
              {icon.name}
            </span>
          </button>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">
        Icons shown in your car colour ({carColor || 'default'})
      </p>
    </div>
  );
};

export { CAR_ICONS, COLOR_MAP, getColorHex, getColorFilter };
export default CarIconSelector;
